import React, { PureComponent } from 'react';
import moment from 'moment';

import { activateUserDetailed } from '../actions/app-props';

import { getCurrentUserId, getCurrentTeam } from '../services/store-service';
import { startConversation } from '../services/chat-service';
import { canUser } from '../services/permissions-service';
import { getRoleLabel } from '../utils/user';

import UserContainer from '../containers/user-container';

import CanUser from './can-user'
import Tooltip from './tooltip';
import AvatarBase from './avatar-base';

const UserEntry = props => {
    const handleClick = () => {
        props.onClick && props.onClick(props.id);
    }

    return (
        <div className="user-entry" onClick={handleClick}>
            <div className="user-entry-head">
                <AvatarBase size="medium" color={props.color} image={props.avatar} text={props.initials} />
            </div>

            <div className="user-entry-body">
                <div className="user-entry-body-main">
                    {props.name}
                </div>

                {
                    props.removed ? (
                        <div className="user-entry-body-meta">
                            no longer in team
                        </div>
                    ) : (
                        props.role ? (
                            <div className="user-entry-body-meta">
                                {getRoleLabel(props.role) + (props.pending ? ' (pending)' : '')}
                            </div>
                        ) : null
                    )
                }
            </div>
        </div>
    )
}

export default class Avatar extends PureComponent {
    handleClick = () => {
        if (this.props.onClick) {
            this.props.onClick(this.props.id);
        } else {
            this.tooltipCtrl && this.tooltipCtrl.show(0);
        }
    }

    handleUserProfileClick = () => {
        this.tooltipCtrl.hide();

        activateUserDetailed(this.props.id);
    }

    handleStartConversationClick = () => {
        startConversation(this.props.id)
    }

    handleSubtitleClick = () => {
        this.props.onSubtitleClick(this.props.id);
    }

    get tooltipContent() {
        if (this.props.tooltipContent) {
            return this.props.tooltipContent(this.props);
        }

        return (
            <div className="popout-inner">
                <div className="user-card">
                    <div className="card-body">
                        <div className="card-body-side">
                            <AvatarBase
                                image={this.props.image}
                                color={this.props.color}
                                secondaryColor={this.props.secondaryColor || this.props.secondary_color}
                                text={this.props.initials}
                                size={'xlarge'}
                            />
                        </div>

                        <div className="card-body-main">
                            <h5><span>{this.props.name}</span> <i className={'status-indicator' + (this.props.online ? ' active' : '')} /></h5>

                            {
                                this.props.role && !this.props.subtitle ? <h6>{getRoleLabel(this.props.role) + (this.props.pending ? ' (pending)' : '')}</h6> : null
                            }

                            {
                                this.props.subtitle ? <h6 className={'subtitle' + (this.props.onSubtitleClick ? ' action' : '')} onClick={this.props.onSubtitleClick && this.handleSubtitleClick}>{this.props.subtitle} {this.props.subtitleIcon}</h6> : null
                            }

                            {
                                this.props.additional ? <h6 className="additional">{this.props.additional}</h6> : null
                            }
                        </div>
                    </div>

                    <div className="card-foot">
                        <div className="card-meta">
                            <i className="icon material-icons">email</i>

                            { this.props.email }
                        </div>

                        {
                            this.props.role && this.props.subtitle ? (
                                <div className="card-meta">
                                    <i className="icon material-icons">person</i>

                                    {getRoleLabel(this.props.role) + (this.props.pending ? ' (pending)' : '')}
                                </div>
                            ) : null
                        }

                        {
                            this.props.timezone ? (
                                <div className="card-meta">
                                    <i className="icon material-icons">access_time</i>

                                    <span>{moment().utcOffset((this.props.timezone || 0) * -1).format('h:mma')} (local time)</span>
                                </div>
                            ) : null
                        }

                        {
                            !this.props.removed ? (
                                <div className="card-cta">
                                    <button className="btn btn-block btn-xsmall btn-secondary" onClick={this.handleUserProfileClick}>
                                        <i className="icon material-icons">person</i>

                                        See profile
                                    </button>

                                    <CanUser type="viewChat">
                                        {
                                            !getCurrentTeam().chat_disabled && this.props.id !== getCurrentUserId() && canUser('viewChat', [], {role: this.props.role, id: this.props.id}) && !this.props.pending ? (
                                                <button className="btn btn-block btn-xsmall btn-secondary" onClick={this.handleStartConversationClick}>
                                                    <i className="icon ion ion-chatboxes" />

                                                    Send message
                                                </button>
                                            ) : null
                                        }
                                    </CanUser>
                                </div>
                            ) : null
                        }
                    </div>
                </div>
            </div>
        )
    }

    get avatarElement() {
        return <AvatarBase
            className={this.props.className}
            size={this.props.size}
            empty={this.props.empty}
            solidImage={this.props.solidImage}
            border={this.props.border}
            image={this.props.image}
            color={this.props.color}
            secondaryColor={this.props.secondaryColor || this.props.secondary_color}
            text={this.props.text}
            dimmed={this.props.removed}
            onClick={this.handleClick}
        />;
    }

    get statusElement() {
        if (!this.props.showStatus || this.props.removed) {
            return null;
        }

        const isOnline = this.props.online || this.props.id === getCurrentUserId();

        return (
            <Tooltip tooltip={(isOnline ? ' Online' : ' Offline')}>
                <span className={'status-indicator' + (isOnline ? ' active' : ' inactive')} />
            </Tooltip>
        );
    }

    render() {
        if (this.props.loading) {
            return null;
        }

        if (this.props.tooltip && !this.props.removed) {
            return (
                <Tooltip
                    type="popout"
                    showDelay={200}
                    provideController={ctrl => this.tooltipCtrl = ctrl}
                    placement={this.props.tooltipPlacement}
                    onShow={this.handleTooltipShow}
                    onHide={this.handleTooltipHide}
                    tooltip={this.tooltipContent}
                >
                    <span className="avatar-wrap tooltip-trigger">
                        { this.avatarElement }

                        { this.statusElement }
                    </span>
                </Tooltip>
            )
        }

        if (this.props.users) {
            return (
                <Tooltip
                    type="popout"
                    showDelay={200}
                    placement="bottom"
                    provideController={ctrl => this.tooltipCtrl = ctrl}
                    onHide={this.handleTooltipHide}
                    tooltip={
                        <div className="popout-inner">
                            <div className="project-users-list simple">
                                { this.props.users.map((userId, index) => <UserContainer key={index} id={userId}><UserEntry onClick={this.props.onClick} /></UserContainer>) }
                            </div>
                        </div>
                    }
                >
                    <span className="avatar-wrap tooltip-trigger">
                        { this.avatarElement }

                        { this.statusElement }
                    </span>
                </Tooltip>
            )
        }

        return (
            <span className="avatar-wrap">
                { this.avatarElement }

                { this.statusElement }
            </span>
        );
    }
}
